import * as React from 'react';
import { notification } from 'antd';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { InfinityTable, Icon, ColumnsFilterModal, Loader, TableHeader, Button } from 'react-inv-forms';

import utilityActions from '../../../redux/utility/actions';
import { NotificationData } from '../../../redux/utility/types';
import { getFiltersConfigSuccess } from '../../../redux/staticViews/actions';
import { getUserIsFromWKD } from '../../../redux/auth/selectors';
import { RootState } from '../../../redux/reducers';
import { getStaticViewIsWKD } from '../../../redux/staticViews/selectors';

import { SingleFilterWKD } from './components';

import withCommonTableLogic, { WithCommonTableLogicProps } from '../../../hoc/withCommonTableLogic';
import withTableClickableModalsHandler, {
  WithTableClickableModalsHandlerProps,
} from '../../../hoc/withTableClickableModalsHandler';
import withContextSearch, { WithContextSearchProps } from '../../../hoc/withContextSearch';
import withViewDataSetHandler, { WithViewDataSetHandlerProps } from '../../../hoc/withViewDataSetHandler';
import withDynamicTabLauncher, { WithDynamicTabLauncherProps } from '../../../hoc/withDynamicTabLauncher';

import { TabConfig } from '../../../interfaces/global';
import { HeaderElement } from '../../../interfaces/global';
import ColumnsAdapter from '../../../helpers/columnsAdapter';
import TableCellTypes from '../../../helpers/tableCellTypes';
import { columnsFilterModalTranslations } from '../../../settings/i18n/translations/packets';

import { FlexBox, TableContainer, AboveTableContainer } from '../../../styles/tabContainers';
import { ContextSearch } from '../../../styles/sharedUIElemStyles';
import filterColumnsModal from '../../../images/filterColumnsModal.svg';

const { generateNonBreakColumns } = ColumnsAdapter;
const { showNotification } = utilityActions;

export type Props = StaticViewComponent &
  MapDispatchToProps &
  MapStateToProps &
  RouteComponentProps &
  WithCommonTableLogicProps &
  WithContextSearchProps &
  WithTableClickableModalsHandlerProps &
  WithViewDataSetHandlerProps &
  WithDynamicTabLauncherProps &
  WithTranslation;

/**
 * Główny komponent zakładki Karty Przedmiotów zawierający m.in. tabelę z kartami przedmiotów przypisanymi do danego prowadzącego.
 * Wszystkie właściwości komponentu pochodzą ze stanu aplikacji i komponentów wyższego rzędu.
 *
 */
export class SubjectsCards extends React.Component<Props> {
  filterWKD = [
    {
      active: true,
      defaultValue: this.props.filterStateWKD.toString(),
      error: false,
      isDefault: true,
      name: 'wkdFilter',
      title: 'FILTER_NAME_WKD',
      type: 'radio',
      values: [
        { nazwa: 'WKD_MY_SUBJECTS', value: false.toString() },
        { nazwa: 'WKD_WKD_SUBJECTS', value: true.toString() },
      ],
    },
  ];

  componentDidMount(): void {
    this.props.getFiltersConfigSuccess(this.props.view, this.filterWKD);
  }

  /* Otwarcie karty pojedynczego przedmiotu */
  openSubjectTab = ({ key, przedmiotNazwa, czyWersja2019, czyWersja2020 }: TabConfig): void => {
    if (czyWersja2019 === 'T' || czyWersja2020 === 'T') {
      this.props.openNewTab(
        {
          titleTranslationKey: przedmiotNazwa,
          tabType: 'karta-przedmiotu',
          itemId: key.toString(),
        },
        this.props.history,
      );
    } else {
      this.props.showNotification({
        notifType: 'warning',
        titleTranslationKey: 'SUBJECT_CARD_EMPTY_CARD_NO_VERSIONS_TITLE',
        bodyTranslationKey: 'SUBJECT_CARD_EMPTY_CARD_NO_VERSIONS_BODY',
      });
    }
  };

  generateTableHeaders = (total: number, availableRecordsAmount: number): HeaderElement[] => [
    {
      title: this.props.t('TABLE_HEADER_TOTAL'),
      info: total,
    },
    {
      title: this.props.t('TABLE_HEADER_DISPLAYED'),
      info: availableRecordsAmount,
    },
    {
      title: this.props.t('TABLE_HEADER_REMAINDER'),
      info: total - availableRecordsAmount,
    },
  ];

  generateScrollContent = (): { x: string; y: string } => {
    return this.props.isWkd
      ? {
          x: 'max-content',
          y: window.outerHeight >= 667 ? 'calc(100vh - 420px)' : '440px',
        }
      : {
          x: 'max-content',
          y: window.outerHeight >= 667 ? 'calc(100vh - 380px)' : '400px',
        };
  };

  render(): React.ReactElement {
    const { loading, selected, totalRecords, search, data, userColumnsSetup, columns } = this.props.state;
    return (
      <div>
        <AboveTableContainer>
          <FlexBox justify='flex-end'>
            <ContextSearch
              onChange={this.props.onContextSearchChange.bind(null, this.props.getDataStart, this.props.setSearch)}
              defaultValue={search}
              placeholder={this.props.t('START_TYPING_SHORT')}
              data-cy='contextSearch_subjectsCards'
            />
          </FlexBox>
          {this.props.isWkd ? (
            <SingleFilterWKD
              convertDataForFilters={this.props.convertDataForFilters}
              openFilterCategory={this.props.openFilterCategory}
              closeFilterCategory={this.props.closeFilterCategory}
              selectFilterValues={this.props.selectFilterValues}
              state={this.props.state}
            />
          ) : null}
          <FlexBox justify='space-between' alignItems='flex-end'>
            <TableHeader data={this.generateTableHeaders(totalRecords, data.length)} />
          </FlexBox>
        </AboveTableContainer>
        <TableContainer loading={loading}>
          <Button
            iconButton
            noPadding
            outlined
            disabled={!columns.length || !data.length}
            className='btn_no_hover'
            data-cy='btnColumnFilter_subjectsCards'
          >
            <Icon
              className={`icon_column-filter-modal ${loading ? 'hidden' : ''}`}
              onClick={this.props.openModal.bind(null, 'filterColumns')}
              path={filterColumnsModal}
              dataCy='btnModalColumnsFilterOpen_subjectsCards'
            />
          </Button>
          <ColumnsFilterModal
            mainColumns={this.props.convertDataForCheckboxes(columns)}
            detailsColumns={this.props.convertDataForCheckboxes(columns)}
            selectedMainColumns={userColumnsSetup.main}
            selectedDetailColumns={userColumnsSetup.details}
            opened={this.props.openedModals.includes('filterColumns')}
            onFilterConfirmation={this.props.saveColumnsUserSetup}
            onClose={this.props.closeModal}
            translations={columnsFilterModalTranslations}
            onError={() =>
              notification.error({
                message: this.props.t('COLUMNS_FILTER_MODAL_SELECT_MIN_ONE_ERROR'),
                top: 100,
              })
            }
            detailsOnly={true}
          />

          {data.length ? (
            <InfinityTable
              tableLayout='auto'
              rowKey={'key'}
              columns={this.props.columnDataWithTranslation(userColumnsSetup.main, generateNonBreakColumns(columns))}
              dataSource={TableCellTypes.generateTableContent(data, columns)}
              pagination={false}
              onRow={(record: any): any => this.props.onSelectedRow(selected, record.key, this.openSubjectTab.bind(null, record))}
              onChange={(pagination: any, filters: any, sorter: any): void => {
                this.props.sortMe(sorter, this.props.setOrder);
              }}
              expandedRowKeys={selected}
              loading={this.props.state.loadingPage}
              loadingIndicator={<Loader style={{ fontSize: '24px', border: '8px solid transparent' }} />}
              showSorterTooltip={false}
              className='table__subjectsCards'
              scroll={this.generateScrollContent()}
              pageSize={selected.length === 1 ? data.length + 1 : data.length}
              onFetch={this.props.state.page < this.props.state.totalPage ? this.props.getDataNextPage : () => {}}
            />
          ) : loading ? null : (
            <span className='block-centered'>{this.props.t('NO_DATA')}</span>
          )}
        </TableContainer>
        {loading && !data.length && (
          <FlexBox justify='center'>
            <Loader style={{ fontSize: '32px', padding: '20px' }} />
          </FlexBox>
        )}
      </div>
    );
  }
}

interface MapDispatchToProps {
  showNotification: (payload: NotificationData) => void;
  getFiltersConfigSuccess: (view: StaticView, data: any[]) => void;
}

const mapDispatchToProps = (dispatch: any, props: Props): MapDispatchToProps => ({
  showNotification: (payload: NotificationData) => dispatch(showNotification(payload)),
  getFiltersConfigSuccess: (view: StaticView, data: any[]) => dispatch(getFiltersConfigSuccess(view, data)),
});

type MapStateToProps = Pick<AppConfig.User, 'isWkd'> & {
  filterStateWKD: boolean;
};

const mapStateToProps = (state: RootState): MapStateToProps => ({
  isWkd: getUserIsFromWKD(state),
  filterStateWKD: getStaticViewIsWKD(state, 'SubjectsCards'),
});

export default compose(
  withTranslation(),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withCommonTableLogic,
  withTableClickableModalsHandler,
  withContextSearch,
  withViewDataSetHandler,
  withDynamicTabLauncher,
)(SubjectsCards) as React.ComponentType;
